import { Element, type PinSlot, type ThermalSpec } from './element';
import type { MnaMatrix } from './mna';
import { THERMAL_V, limitJunction } from './elements';
import { fmt } from './format';

/* ═══════════════════════════ BJT (Ebers-Moll) ═══════════════════════════ */

/**
 * 바이폴라 트랜지스터. 핀: B(베이스), C(컬렉터), E(이미터).
 * 수송(transport) 형태의 Ebers-Moll 모델, 얼리 효과는 생략.
 */
export class Bjt extends Element {
  private vbe = 0;
  private vbc = 0;
  private ib = 0;
  private ic = 0;

  override get pinCount(): number {
    return 3;
  }
  override pinLayout(): PinSlot[] {
    return [
      { t: 0, s: 0, label: 'B' },
      { t: 'end', s: -1, label: 'C' },
      { t: 'end', s: 1, label: 'E' },
    ];
  }
  override get nonlinear(): boolean {
    return true;
  }
  get isPnp(): boolean {
    return this.type === 'pnp';
  }
  private pol(): number {
    return this.isPnp ? -1 : 1;
  }
  private is(): number {
    return Math.max(this.num('is', 1e-14), 1e-20);
  }
  private bf(): number {
    return Math.max(this.num('beta', 100), 1);
  }
  private br(): number {
    return 1;
  }

  override resetState(): void {
    this.vbe = 0;
    this.vbc = 0;
    this.ib = 0;
    this.ic = 0;
  }

  private evalAt(vbe: number, vbc: number) {
    const is = this.is();
    const vt = THERMAL_V;
    const bf = this.bf();
    const br = this.br();
    const a = Math.exp(Math.min(vbe / vt, 80));
    const c = Math.exp(Math.min(vbc / vt, 80));
    const gF = (is / vt) * a;
    const gR = (is / vt) * c;
    return {
      ic: is * (a - c) - (is / br) * (c - 1),
      ib: (is / bf) * (a - 1) + (is / br) * (c - 1),
      gcbe: gF,
      gcbc: -gR - gR / br,
      gbbe: gF / bf,
      gbbc: gR / br,
    };
  }

  /** TO-92 패키지: 200℃/W, 150℃ 넘으면 파손 */
  override thermalSpec(): ThermalSpec | null {
    return { rth: 200, cth: 5 / 200, tmax: 150 };
  }

  override stamp(m: MnaMatrix): void {
    const [b, c, e] = this.nodes;
    if (this.burnt) {
      m.conductance(b, e, 1e-12);
      m.conductance(c, e, 1e-12);
      return;
    }
    const pol = this.pol();
    const { vbe, vbc } = this;
    const r = this.evalAt(vbe, vbc);

    const Cc = pol * (r.ic - r.gcbe * vbe - r.gcbc * vbc);
    const Cb = pol * (r.ib - r.gbbe * vbe - r.gbbc * vbc);
    const rowC = [r.gcbe + r.gcbc, -r.gcbc, -r.gcbe];
    const rowB = [r.gbbe + r.gbbc, -r.gbbc, -r.gbbe];
    const rowE = rowC.map((g, i) => -(g + rowB[i]));

    // 순서: [B, C, E]
    m.linearized([b, c, e], [rowB, rowC, rowE], [Cb, Cc, -(Cb + Cc)]);
    m.conductance(b, e, 1e-12);
    m.conductance(b, c, 1e-12);
  }

  override updateNewton(m: MnaMatrix): number {
    if (this.burnt) return 0;
    const [b, c, e] = this.nodes;
    const pol = this.pol();
    const vb = m.nodeVoltage(b);
    const vc = m.nodeVoltage(c);
    const ve = m.nodeVoltage(e);
    const vt = THERMAL_V;
    const vcrit = vt * Math.log(vt / (Math.SQRT2 * this.is()));
    const nbe = limitJunction(pol * (vb - ve), this.vbe, vt, vcrit);
    const nbc = limitJunction(pol * (vb - vc), this.vbc, vt, vcrit);
    const delta = Math.max(Math.abs(nbe - this.vbe), Math.abs(nbc - this.vbc));
    this.vbe = nbe;
    this.vbc = nbc;
    return delta;
  }

  override endStep(m: MnaMatrix): void {
    const [, c, e] = this.nodes;
    const r = this.evalAt(this.vbe, this.vbc);
    const pol = this.pol();
    this.ic = this.burnt ? 0 : pol * r.ic;
    this.ib = this.burnt ? 0 : pol * r.ib;
    this.current = this.ic;
    this.voltageDrop = m.nodeVoltage(c) - m.nodeVoltage(e);
  }

  override pinCurrents(): number[] {
    return [this.ib, this.ic, -(this.ib + this.ic)];
  }

  override get power(): number {
    // Vce·Ic + Vbe·Ib (대략적인 발열)
    return Math.abs(this.voltageDrop * this.ic) + Math.abs(this.vbe * this.ib);
  }

  region(): string {
    const pol = this.pol();
    const vce = pol * this.voltageDrop;
    if (this.vbe < 0.5) return '차단 (OFF)';
    if (vce < 0.25) return '포화 (완전 ON)';
    return '활성 (증폭)';
  }

  info(): string[] {
    const beta = Math.abs(this.ib) > 1e-12 ? this.ic / this.ib : 0;
    return [
      this.isPnp ? 'PNP 트랜지스터' : 'NPN 트랜지스터',
      `동작 ${this.region()}`,
      `Vbe ${fmt(this.pol() * this.vbe, 'V')}`,
      `Vce ${fmt(this.voltageDrop, 'V')}`,
      `Ib ${fmt(this.ib, 'A')}`,
      `Ic ${fmt(this.ic, 'A')}`,
      `실제 β ${beta ? beta.toFixed(1) : '—'}`,
    ];
  }
}

/* ═══════════════════════════ MOSFET (Level 1) ═══════════════════════════ */

/** MOSFET. 핀: G(게이트), D(드레인), S(소스). 제곱 법칙 모델 */
export class Mosfet extends Element {
  private vgs = 0;
  private vds = 0;
  private swapped = false;
  private idrain = 0;

  override get pinCount(): number {
    return 3;
  }
  override pinLayout(): PinSlot[] {
    return [
      { t: 0, s: 0, label: 'G' },
      { t: 'end', s: -1, label: 'D' },
      { t: 'end', s: 1, label: 'S' },
    ];
  }
  override get nonlinear(): boolean {
    return true;
  }
  get isPmos(): boolean {
    return this.type === 'pmos';
  }
  private pol(): number {
    return this.isPmos ? -1 : 1;
  }
  private vth(): number {
    return Math.abs(this.num('vth', 2));
  }
  private beta(): number {
    return Math.max(this.num('beta', 0.5), 1e-9);
  }
  private lambda(): number {
    return Math.max(this.num('lambda', 0.01), 0);
  }

  override resetState(): void {
    this.vgs = 0;
    this.vds = 0;
    this.swapped = false;
    this.idrain = 0;
  }

  private evalAt(vgs: number, vds: number) {
    const vov = vgs - this.vth();
    const beta = this.beta();
    const lam = this.lambda();
    if (vov <= 0) return { id: 0, gm: 0, gds: 0 };
    const k = 1 + lam * vds;
    if (vds < vov) {
      const base = vov * vds - (vds * vds) / 2;
      return { id: beta * base * k, gm: beta * vds * k, gds: beta * (vov - vds) * k + beta * base * lam };
    }
    return { id: (beta / 2) * vov * vov * k, gm: beta * vov * k, gds: (beta / 2) * vov * vov * lam };
  }

  /** TO-220 (방열판 없음): 62℃/W, 175℃ 넘으면 파손 */
  override thermalSpec(): ThermalSpec | null {
    return { rth: 62, cth: 10 / 62, tmax: 175 };
  }

  override stamp(m: MnaMatrix): void {
    const [g, d, s] = this.nodes;
    if (this.burnt) {
      m.conductance(d, s, 1e-12);
      m.conductance(g, s, 1e-12);
      return;
    }
    const pol = this.pol();
    const r = this.evalAt(this.vgs, this.vds);
    const Cd = pol * (r.id - r.gm * this.vgs - r.gds * this.vds);

    // 실효 드레인/소스 (vds<0 이면 역할이 바뀐다)
    const iD = this.swapped ? 2 : 1;
    const iS = this.swapped ? 1 : 2;
    const rowD = [0, 0, 0];
    rowD[0] = r.gm;
    rowD[iD] = r.gds;
    rowD[iS] = -(r.gm + r.gds);
    const rows = [[0, 0, 0], [0, 0, 0], [0, 0, 0]];
    rows[iD] = rowD;
    rows[iS] = rowD.map((x) => -x);
    const C = [0, 0, 0];
    C[iD] = Cd;
    C[iS] = -Cd;
    m.linearized([g, d, s], rows, C);
    m.conductance(d, s, 1e-9);
  }

  override updateNewton(m: MnaMatrix): number {
    if (this.burnt) return 0;
    const [g, d, s] = this.nodes;
    const pol = this.pol();
    const vg = m.nodeVoltage(g);
    const vd = m.nodeVoltage(d);
    const vs = m.nodeVoltage(s);
    const raw = pol * (vd - vs);
    const swapped = raw < 0;
    let vgs = pol * (vg - (swapped ? vd : vs));
    let vds = Math.abs(raw);
    if (swapped === this.swapped) {
      // 한 번에 너무 크게 움직이지 않게 제한
      vgs = this.vgs + clamp(vgs - this.vgs, -2, 2);
      vds = this.vds + clamp(vds - this.vds, -5, 5);
    }
    const delta = Math.max(Math.abs(vgs - this.vgs), Math.abs(vds - this.vds)) + (swapped !== this.swapped ? 1 : 0);
    this.vgs = vgs;
    this.vds = vds;
    this.swapped = swapped;
    return delta;
  }

  override endStep(m: MnaMatrix): void {
    const [, d, s] = this.nodes;
    const r = this.evalAt(this.vgs, this.vds);
    const pol = this.pol();
    // 실제 드레인 단자로 들어가는 전류
    this.idrain = this.burnt ? 0 : (this.swapped ? -1 : 1) * pol * r.id;
    this.current = this.idrain;
    this.voltageDrop = m.nodeVoltage(d) - m.nodeVoltage(s);
  }

  override pinCurrents(): number[] {
    return [0, this.idrain, -this.idrain];
  }

  override get power(): number {
    return Math.abs(this.voltageDrop * this.idrain);
  }

  region(): string {
    const vov = this.vgs - this.vth();
    if (vov <= 0) return '차단 (OFF)';
    return this.vds < vov ? '선형 (완전 ON)' : '포화 (증폭)';
  }

  info(): string[] {
    const pol = this.pol();
    return [
      this.isPmos ? 'P-MOSFET' : 'N-MOSFET',
      `동작 ${this.region()}`,
      `Vgs ${fmt(pol * this.vgs, 'V')} (문턱 ${fmt(pol * this.vth(), 'V')})`,
      `Vds ${fmt(this.voltageDrop, 'V')}`,
      `Id ${fmt(this.idrain, 'A')}`,
      `발열 ${fmt(this.power, 'W')}`,
    ];
  }
}

/* ═══════════════════════════ 연산증폭기 ═══════════════════════════ */

/**
 * 연산증폭기. 핀: −입력, +입력, 출력.
 * 이득 A 의 전압제어 전압원인데, 출력이 전원 레일을 넘지 못하게 tanh 로 부드럽게 포화시킨다.
 * 전원핀은 생략(출력은 접지 기준).
 */
export class OpAmp extends Element {
  private vd = 0;
  private iout = 0;

  override get usesImplicitGround(): boolean {
    return true;
  }

  override get pinCount(): number {
    return 3;
  }
  override pinLayout(): PinSlot[] {
    return [
      { t: 0, s: -1, label: '−' },
      { t: 0, s: 1, label: '+' },
      { t: 'end', s: 0, label: 'OUT' },
    ];
  }
  override get nonlinear(): boolean {
    return true;
  }
  override get voltageSourceCount(): number {
    return 1;
  }
  override resetState(): void {
    this.vd = 0;
    this.iout = 0;
  }

  private transfer(vd: number): { out: number; slope: number } {
    const gain = Math.max(this.num('gain', 1e5), 1);
    const vp = this.num('vpos', 12);
    const vn = Math.min(this.num('vneg', -12), vp - 0.01);
    const mid = (vp + vn) / 2;
    const half = (vp - vn) / 2;
    const t = Math.tanh((gain * vd - mid) / half);
    return { out: mid + half * t, slope: gain * (1 - t * t) };
  }

  override stamp(m: MnaMatrix): void {
    const [n, p, o] = this.nodes;
    const r = m.n + this.vsIndex;
    const { out, slope } = this.transfer(this.vd);
    m.addA(o, r, 1);
    m.addA(r, o, 1);
    m.addA(r, p, -slope);
    m.addA(r, n, slope);
    m.b[r] += out - slope * this.vd;
  }

  override updateNewton(m: MnaMatrix): number {
    const [n, p] = this.nodes;
    const vd = m.nodeVoltage(p) - m.nodeVoltage(n);
    const delta = Math.abs(vd - this.vd);
    this.vd = vd;
    // 출력 기준 변화량으로 수렴 판정 (이득이 커서 입력 차이는 아주 작다)
    return delta * Math.min(this.num('gain', 1e5), 1e3);
  }

  override endStep(m: MnaMatrix): void {
    const [, , o] = this.nodes;
    this.iout = m.branchCurrent(this.vsIndex);
    this.current = -this.iout;
    this.voltageDrop = m.nodeVoltage(o);
  }

  override pinCurrents(): number[] {
    return [0, 0, this.iout];
  }

  get outputVoltage(): number {
    return this.voltageDrop;
  }

  info(): string[] {
    const sat = Math.abs(this.transfer(this.vd).slope) < 1e-3 * this.num('gain', 1e5);
    return [
      '연산증폭기',
      `V+ − V− ${fmt(this.vd, 'V')}`,
      `출력 ${fmt(this.voltageDrop, 'V')}${sat ? ' (포화)' : ''}`,
      `출력 전류 ${fmt(-this.iout, 'A')}`,
      `레일 ${fmt(this.num('vneg', -12), 'V')} ~ ${fmt(this.num('vpos', 12), 'V')}`,
    ];
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}
